import type { MediaTableRow } from '@pages/Library/components/LibraryContentMediaTable/cells/types';

/**
 * Pure YouTube / hostname helpers. Kept free of asset (PNG) imports so they can be
 * imported by standalone logic (and unit tests run via `tsx`) without dragging in
 * the platform-icon graph in `platforms.ts`. Re-exported from `platforms.ts` for
 * back-compat, so existing `@lib/utils/platforms` importers keep working.
 */

/**
 * Returns the YouTube video ID from a media item's origin, or null for non-YouTube media.
 */
export const getYouTubeVideoId = (media: MediaTableRow): string | null => {
  if (!media.origin || media.origin.source !== 'external' || media.origin.type !== 'youtube') {
    return null;
  }
  return media.origin.id ?? null;
};

/**
 * Checks whether a hostname matches a given domain.
 * By default also matches subdomains (e.g. www.youtube.com matches 'youtube.com').
 * Pass { allowSubdomains: false } for exact-only matching.
 */
export const isHostname = (
  hostname: string,
  domain: string,
  { allowSubdomains = true }: { allowSubdomains?: boolean } = {},
): boolean => hostname === domain || (allowSubdomains && hostname.endsWith('.' + domain));

/**
 * Extracts a YouTube video ID from a string that is either a raw ID or a YouTube URL.
 */
export const extractYouTubeId = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Try parsing as URL first
  try {
    const urlObj = new URL(trimmed);
    const isYouTube =
      isHostname(urlObj.hostname, 'youtu.be', { allowSubdomains: false }) ||
      isHostname(urlObj.hostname, 'youtube.com') ||
      isHostname(urlObj.hostname, 'youtube-nocookie.com');

    if (isYouTube) {
      if (isHostname(urlObj.hostname, 'youtu.be', { allowSubdomains: false })) {
        const id = urlObj.pathname.split('/')[1];
        return id && id.length >= 11 ? id : null;
      }

      const v = urlObj.searchParams.get('v');
      if (v && v.length >= 11) return v;

      for (const prefix of ['/v/', '/embed/', '/e/', '/shorts/', '/live/']) {
        if (urlObj.pathname.startsWith(prefix)) {
          const id = urlObj.pathname.split('/')[2];
          if (id && id.length >= 11) return id;
        }
      }
    }
  } catch {
    // Not a URL — treat as raw ID
  }

  // Bare YouTube ID: currently always 11 chars. The >= 11 checks above are intentionally
  // lenient for URL-extracted IDs (the URL structure already validates them), while
  // the strict === 11 regex here prevents false positives on arbitrary strings.
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;

  return null;
};
